import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Apollo, gql } from 'apollo-angular';
import { Observable, catchError, combineLatest, map, of, switchMap, take } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  mapBankAccountToIBankDetails,
  mapGraphQLGuardianToIAccountHolder,
} from '../../graphql/functions/mappingFunctions';
import { Data, IBursar } from '../../graphql/models/gqlModels';
import { BankRecord, BankStatement, IBankDetails } from '../../models/finance';
import { AccountLinks, IAccountHolder } from '../../models/user';
import { selectSelectedAccountLinks } from '../../store/finance/finance.selectors';
import { selectGuardians } from '../../store/guardian/guardian.selectors';

@Injectable({
  providedIn: 'root',
})
export class FinanceService {
  constructor(
    private readonly apollo: Apollo,
    private readonly store: Store,
  ) {}

  getBankingDetails(accountLink: AccountLinks): Observable<IBankDetails[]> {
    const query = gql`
      query BankQuery {
        companySearch(criteria: {companyCode: "${accountLink.companyCode}"}) {
          content {
            companyConfig {
              bankAccounts {
                bankDetails {
                  accountName
                  accountNumber
                  bankName
                  branchCode
                  costCenterCode
                  costCenterName
                  id
                  swiftCode
                  bankAccountType {
                    description
                    id
                    refDataType
                    shortCode
                  }
                }
                accountPurposeType {
                  description
                  id
                  refDataType
                  shortCode
                }
                accountNo
              }
            }
            myHubCompanySettings {
              companyCode
              companyName
            }
          }
        }
      }
    `;

    return this.apollo.query<Data>({ query }).pipe(
      map((response) => {
        if (!response.data?.companySearch?.content?.[0]?.companyConfig?.bankAccounts) {
          console.warn(`No banking details found for company code: ${accountLink.companyCode}`);
          return [];
        }
        const bankAccounts = response.data.companySearch.content[0].companyConfig.bankAccounts;
        const companyName = response.data.companySearch.content[0].myHubCompanySettings?.companyName;
        return mapBankAccountToIBankDetails(
          bankAccounts,
          accountLink.accountCode,
          accountLink.companyCode,
          companyName,
        );
      }),
      catchError((error) => {
        console.error(`Failed to fetch banking details for company code ${accountLink.companyCode}:`, error);
        return of([]); // Return empty array to prevent breaking the stream
      }),
    );
  }

  getBalancesForAccounts(links: AccountLinks[]): Observable<BankRecord[]> {
    const query = gql`
      query GetBalances($accountCode: String!, $companyCode: String!, $today: String!) {
        fetchBalance(accountCode: $accountCode, companyCode: $companyCode, date: $today) {
          balance
          active
          currencyCode
        }
      }
    `;

    const today = new Date().toISOString().split('T')[0]; // Format: YYYY-MM-DD

    // Create an array of observables for each account
    const balanceObservables = links.map((link) =>
      this.apollo
        .query<{ fetchBalance: { balance: number; active: boolean; currencyCode: string } }>({
          query,
          variables: {
            accountCode: link.accountCode,
            companyCode: link.companyCode,
            today,
          },
          context: {
            uri: `${environment.apiBaseUrl}/finance/data/graphql`,
          },
        })
        .pipe(
          map((response) => {
            if (response.errors && response.errors.length > 0) {
              console.warn(`GraphQL returned errors for account ${link.accountCode}:`, response.errors);
            }

            const balance = response.data?.fetchBalance?.balance ? Number(response.data.fetchBalance.balance) : 0;
            const active = response.data?.fetchBalance?.active ?? false;
            const currencyCode = response.data?.fetchBalance?.currencyCode ?? 'ZAR';

            return {
              accountLinks: link,
              balance,
              active,
              currencyCode,
              statements: [], // Optional property, empty array for balance-only records
            };
          }),
          catchError((error) => {
            console.error(`Failed to fetch balance for account ${link.accountCode}:`, error);
            return of({
              accountLinks: link,
              balance: 0,
              currencyCode: 'ZAR',
              statements: [],
            });
          }),
        ),
    );

    // Combine all observables and return as BankRecord array
    return combineLatest(balanceObservables);
  }

  getBankRecords(links: AccountLinks, fromDate: Date, toDate: Date): Observable<BankRecord> {
    const query = gql`
      query GetBankRecord(
        $accountCode: String!
        $companyCode: String!
        $today: String!
        $fromDate: String!
        $toDate: String!
      ) {
        fetchBalance(accountCode: $accountCode, companyCode: $companyCode, date: $today) {
          balance
          currencyCode
        }
        fetchStatements(accountCode: $accountCode, companyCode: $companyCode, fromDate: $fromDate, toDate: $toDate) {
          amount
          balance
          description
          invoiceNumber
          transactionDate
          statementDetails {
            amount
            description
          }
        }
      }
    `;

    const today = new Date().toISOString().split('T')[0]; // Format: YYYY-MM-DD

    return this.apollo
      .query<{
        fetchBalance: { balance: number; currencyCode: string };
        fetchStatements: BankStatement[];
      }>({
        query,
        variables: {
          accountCode: links.accountCode,
          companyCode: links.companyCode,
          today,
          fromDate: this.formatDateLocal(fromDate),
          toDate: this.formatDateLocal(toDate),
        },
        context: {
          uri: `${environment.apiBaseUrl}/finance/data/graphql`, // Set custom endpoint for this specific query
        },
      })
      .pipe(
        map((response) => {
          if (response.errors && response.errors.length > 0) {
            console.warn(`GraphQL returned errors for account ${links.accountCode}:`, response.errors);
          }

          const balance = response.data?.fetchBalance?.balance ? Number(response.data.fetchBalance.balance) : 0;
          const currencyCode = response.data?.fetchBalance?.currencyCode ?? 'ZAR';
          const statements = Array.isArray(response.data?.fetchStatements) ? response.data!.fetchStatements : [];

          // Create a BankRecord with both balance and statements
          return {
            accountLinks: links,
            balance,
            currencyCode,
            statements,
          };
        }),
        catchError((error) => {
          console.error(`Failed to fetch bank record for account ${links.accountCode}:`, error);
          // Return a default bank record with empty data
          return of({
            accountLinks: links,
            balance: 0,
            statements: [],
          });
        }),
      );
  }

  getAccountHolderDetails(accountLink: AccountLinks): Observable<IAccountHolder | null> {
    // If accountLink is missing required fields, try to get from store
    if (!accountLink?.accountCode || !accountLink?.companyCode) {
      return this.store.select(selectSelectedAccountLinks).pipe(
        take(1),
        switchMap((selectedAccount) => {
          const validAccount = selectedAccount?.accountCode && selectedAccount?.companyCode ? selectedAccount : null;
          if (!validAccount) {
            console.warn(
              'Cannot fetch account holder details: accountCode or companyCode is missing and no selected account found in store',
            );
            return of(null);
          }
          return this._fetchAccountHolderDetails(validAccount);
        }),
        catchError((error) => {
          console.error('Error retrieving selected account from store:', error);
          return of(null);
        }),
      );
    }

    return this._fetchAccountHolderDetails(accountLink);
  }

  private _fetchAccountHolderDetails(accountLink: AccountLinks): Observable<IAccountHolder | null> {
    const year = new Date().getFullYear();
    const query = gql`
query AccountQuery {
    accountSearch(
      criteria: {accountCode: "${accountLink.accountCode}", allocation: {year: ${year}}, company: {companyCode: "${accountLink.companyCode}"}}
    ) {
      content {
        allocation {
          guardianLinks {
            guardian {
              id
              accountCode
              imageUrl
              person {
                id
                firstName
                lastName
                nickName
                title {
                  description
                }
                phoneNumbers {
                  number
                }
                emailAddresses {
                  address
                }
              }
              relationship {
                description
              }
            }
          }
        }
        company {
          companyCode
          id
          bursar {
            firstName
            lastName
            email
            PhoneNumber
          }
        }
      }
    }
  }
    `;

    // Only return the first guardian where accountHolder is true
    return this.apollo.query<Data>({ query }).pipe(
      switchMap((response) => {
        if (!response.data?.accountSearch?.content?.[0]?.allocation?.[0]?.guardianLinks) {
          console.warn(`No guardian links found for account: ${accountLink.accountCode}`);
          return of(null);
        }

        const guardianLinks = response.data.accountSearch.content[0].allocation[0].guardianLinks;

        // Take a single snapshot of guardians from the store and compute the account holder
        return this.store.select(selectGuardians).pipe(
          take(1),
          map((loadedGuardians) => {
            const accountHolderFromStore =
              loadedGuardians?.find((guardian) =>
                guardian.accountLinks?.some(
                  (link) =>
                    link.accountCode === accountLink.accountCode &&
                    link.companyCode === accountLink.companyCode &&
                    !!link.isAccountHolder,
                ),
              ) || null;

            let accountHolderGuardian = null as any;
            if (accountHolderFromStore) {
              accountHolderGuardian =
                guardianLinks.find((link) => link.guardian?.id === accountHolderFromStore.id)?.guardian || null;
            }

            if (!accountHolderGuardian) {
              console.warn(`No account holder found for account: ${accountLink.accountCode}`);
              return null;
            }

            return mapGraphQLGuardianToIAccountHolder(accountHolderGuardian);
          }),
        );
      }),
      catchError((error) => {
        console.error(`Failed to fetch account holder details for account ${accountLink.accountCode}:`, error);
        return of(null);
      }),
    );
  }

  getAccountBursarDetails(accountLink: AccountLinks): Observable<IBursar | null> {
    const year = new Date().getFullYear();
    const query = gql`
      query AccountQuery {
          accountSearch(
            criteria: {accountCode: "${accountLink.accountCode}", allocation: {year: ${year}}, company: {companyCode: "${accountLink.companyCode}"}}
          ) {
            content {
              company {
                companyCode
                id
                bursar {
                  firstName
                  lastName
                  email
                  PhoneNumber
                }
              }
            }
          }
        }
    `;

    return this.apollo.query<Data>({ query }).pipe(
      map((response) => {
        const bursar = response.data?.accountSearch?.content?.[0]?.company?.bursar;
        if (!bursar) {
          console.warn(`No bursar found for account: ${accountLink.accountCode}`);
          return null;
        }

        return {
          firstName: bursar.firstName ?? '',
          lastName: bursar.lastName ?? '',
          email: bursar.email ?? '',
          PhoneNumber: bursar.PhoneNumber ?? '',
        } as IBursar;
      }),
      catchError((error) => {
        console.error(`Failed to get account burser for account ${accountLink.accountCode}:`, error);
        return of(null);
      }),
    );
  }

  formatDateLocal(date: Date): string {
    // to prevent timezone issues, format date as YYYY-MM-DD using local date parts
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
